import { memo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FacilitatorReport,
  TEACH_IN_REPORT_STORAGE_KEY,
} from "../../config/earthDayExperience";
import "./facilitator-report.scss";

const safeList = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && !!item.trim())
    : [];

const normalizeReport = (value: FacilitatorReport): FacilitatorReport => ({
  ...value,
  strongest_moments: safeList(value.strongest_moments),
  improvement_opportunities: safeList(value.improvement_opportunities),
  agenda_refinements: safeList(value.agenda_refinements),
  materials_preparation: safeList(value.materials_preparation),
  follow_up_actions: safeList(value.follow_up_actions),
  transcript_excerpt: safeList(value.transcript_excerpt),
  transcript: Array.isArray(value.transcript) ? value.transcript : [],
  session_metrics: {
    duration_seconds: value.session_metrics?.duration_seconds ?? 0,
    user_turns: value.session_metrics?.user_turns ?? 0,
    gemini_turns: value.session_metrics?.gemini_turns ?? 0,
    total_turns: value.session_metrics?.total_turns ?? 0,
  },
});

const formatDuration = (durationSeconds: number) => {
  const safeSeconds = Math.max(0, Math.round(durationSeconds));

  if (safeSeconds < 60) {
    return `${safeSeconds}s`;
  }

  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  if (!seconds) {
    return `${minutes} min`;
  }

  return `${minutes} min ${seconds}s`;
};

function FacilitatorReportComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState<FacilitatorReport | null>(null);

  useEffect(() => {
    try {
      const savedReport = localStorage.getItem(TEACH_IN_REPORT_STORAGE_KEY);

      if (!savedReport) {
        return;
      }

      setReport(normalizeReport(JSON.parse(savedReport) as FacilitatorReport));
    } catch {
      setReport(null);
    }
  }, []);

  if (!report) {
    return (
      <div className="facilitator-report-shell">
        <section className="facilitator-report-card empty">
          <span className="report-kicker">Facilitator report</span>
          <h1>No report is ready yet</h1>
          <p>
            Start a live teach-in planning session, end it, and the report will
            appear here with coaching notes and a PDF download.
          </p>
          <div className="report-actions">
            <button
              className="report-primary"
              onClick={() => navigate("/facilitator")}
            >
              Go to facilitator
            </button>
          </div>
        </section>
      </div>
    );
  }

  const transcriptAvailable = report.transcript.length > 0;

  return (
    <div className="facilitator-report-shell">
      {location.state &&
        typeof location.state === "object" &&
        "justGenerated" in location.state && (
          <div className="report-toast">
            Facilitator report generated successfully.
          </div>
        )}

      <section className="facilitator-report-card">
        <div className="report-header">
          <div>
            <span className="report-kicker">Facilitator report</span>
            <h1>{report.title}</h1>
            <p>
              Review the strongest moments, improvement opportunities, and
              follow-up actions from your live Earth Day planning session.
            </p>
          </div>

          <div className="report-score-card">
            <span>Readiness score</span>
            <strong>{report.readiness_score}</strong>
            <em>{report.readiness_label}</em>
          </div>
        </div>

        <div className="report-actions">
          <button
            className="report-primary"
            onClick={() => window.open(report.pdf_download_url, "_blank", "noopener,noreferrer")}
          >
            Download PDF
          </button>
          <button
            className="report-secondary"
            onClick={() => navigate("/facilitator")}
          >
            Back to facilitator
          </button>
        </div>

        <div className="report-overview-grid">
          <article className="report-panel">
            <h2>Session overview</h2>
            <p>{report.session_overview}</p>
          </article>
          <article className="report-panel">
            <h2>Audience fit</h2>
            <p>{report.audience_fit_summary}</p>
          </article>
        </div>

        <div className="report-metrics">
          <div>
            <span>Duration</span>
            <strong>{formatDuration(report.session_metrics.duration_seconds)}</strong>
          </div>
          <div>
            <span>Your turns</span>
            <strong>
              {transcriptAvailable ? report.session_metrics.user_turns : "Not captured"}
            </strong>
          </div>
          <div>
            <span>Gemini turns</span>
            <strong>{report.session_metrics.gemini_turns}</strong>
          </div>
          <div>
            <span>Transcript turns</span>
            <strong>
              {transcriptAvailable ? report.session_metrics.total_turns : "Not captured"}
            </strong>
          </div>
        </div>

        <div className="report-grid">
          <article className="report-panel">
            <h2>Strongest moments</h2>
            <ul>
              {report.strongest_moments.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="report-panel">
            <h2>Improvement opportunities</h2>
            <ul>
              {report.improvement_opportunities.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="report-panel">
            <h2>Agenda refinements</h2>
            <ul>
              {report.agenda_refinements.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="report-panel">
            <h2>Materials and preparation</h2>
            <ul>
              {report.materials_preparation.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="report-panel wide">
            <h2>Follow-up community actions</h2>
            <ul>
              {report.follow_up_actions.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="report-panel wide">
            <h2>Transcript highlights</h2>
            <ul>
              {report.transcript_excerpt.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </div>
  );
}

export default memo(FacilitatorReportComponent);
